import Vue from 'vue';
import { Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { processHelpers, ConversionResults, ProcessConfig } from './../components/page-items/custom/CFtProcesses/index';
import _ from 'lodash';
import { FTProcessRow, FTProcessRowTypes } from './FTProcessRow';
import { FTFactoryTicket } from './FTFactoryTicket';
import { MachineDetail } from './MachineDetail';

export class FTProcess {
  type = '';
  machine = '';
  machineDetail: MachineDetail;
  config: ProcessConfig;
  initialized: boolean;
  index: number;
  inputs: FTProcessRow[] = [];
  outputs: FTProcessRow[] = [];
  settings: any = {};
  conversionDetails: any = {};
  ftFactoryTicket: FTFactoryTicket;
  config$ = new Subject<any>();
  hasRightSideButtons = false;

  constructor(process: any, index: number, ftFactoryTicket: FTFactoryTicket) {
    this.type = process.type && (process.type.name || process.type);
    this.machine = process.machine;
    this.machineDetail = process.machineDetail;
    this.initialized = process.initialized ? process.initialized : false; 
    this.ftFactoryTicket = ftFactoryTicket;
    this.index = index;
    this.config = processHelpers[this.type].loadConfig({ process: process });
    this.parseProcessSettings(process);
    if (this.initialized === false && !process.inputs) {
      const outputObject = new FTProcessRow({
        config: {
          type: FTProcessRowTypes.output,
          key: 0
        }, 
        ftProcess: this,
      });
      this.outputs = [ outputObject ];
      const { goods } = this.config.inputs;
      const inputs: FTProcessRow[] = [];
      _.forEach(goods, (goodValue: any, key: number) => {
        const { goodsType } = goodValue;
        inputs[key] = new FTProcessRow({
          config: { goodsType, type: FTProcessRowTypes.input, key }, 
          ftProcess: this,
        });
      });
      this.inputs = inputs;
      this.initialized = true;
      this.initializeType();
    } else {
      this.inputs = _.map(process.inputs, (input: any, key: number) => new FTProcessRow({
        config: { ...input, type: FTProcessRowTypes.input, key },
        ftProcess: this,
      }) );
      this.addOptionalInputs();
      this.outputs = _.map(process.outputs, (output: any, key: number) => new FTProcessRow({
        config: { ...output, type: FTProcessRowTypes.output, key },
        ftProcess: this,
      }) );

    }
    this.config$.pipe(
      tap(() => {
        this.updateConfig();
        this.hasRightSideButtons = this.getHasRightSideButtonsStatus();
      })
    ).subscribe();
  }

  initializeType() {
    if(this.type === 'Packing' && this.ftFactoryTicket.order.product.packingMode) {
      for( let index = 1; index < this.ftFactoryTicket.order.product.packingMode.length; index++) {
        this.addInput('packing-material', this.inputs.length - 1);
      }
    }
  }

  addOptionalInputs() {
    if(['Paper Printing', 'PSL Printing'].includes(this.type)) {
      _.forEach(['adhesive', 'lamination'], (goodsType) => {
        const goodsKey = _.findIndex(this.inputs, { goodsType });
        if(goodsKey == -1) {
          this.addInput(goodsType, this.inputs.length - 1);
        }
      });
    }
  }

  updateConfig() {
    this.config = processHelpers[this.type].loadConfig({ process: this });
    _.each(this.inputs, (input: FTProcessRow) => {
      input.updateConfig();
    });
    _.each(this.outputs, (output: FTProcessRow) => {
      output.updateConfig();
    });
  }

  parseProcessSettings(process: any) {
    this.updateConfig();
    _.each(this.config.settings, (setting, settingKey) => {
      if(process.settings && process.settings[settingKey]) {
        this.settings[settingKey] = process.settings[settingKey];
      } else if(setting.default) {
        this.settings[settingKey] = setting.default;
      }
    })
  }

  toJSON() {
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    const {config, convert, index, ftFactoryTicket, inputs, config$, hasRightSideButtons,...returnedObjectWithoutInputs } = this;
    // removing empty inputs before submitting. Needed in printing when some inputs are hidden by default.
    const filteredInputs = _.filter(this.inputs, (input: FTProcessRow) => !!input._id)
    const returnedObject = { ...returnedObjectWithoutInputs, inputs: filteredInputs };
    return returnedObject;
  }

  convert() {
    const conversionOutput : ConversionResults = processHelpers[this.type].convert(this);
    _.forEach(conversionOutput.inputs, (input) => input.goodsObject.updateText());
    _.forEach(conversionOutput.outputs, (output) => output.goodsObject.updateText());
    this.conversionDetails = conversionOutput.details;
    this.config$.next();
    return conversionOutput;
  }

  getAddMoreButtonText(goodsType: string): string | false {
    const goodsKey = _.findIndex(this.config.inputs.goods, { goodsType })
    if(goodsKey > -1) {
      return _.get(this.config.inputs.goods[goodsKey], 'addMultipleButtonText');
    } else {
      return false;
    }
  }

  removeProcess() {
    const { processes } = this.ftFactoryTicket;
    const indexToDelete = this.index;
    const deletedProcessOutputs = processes[indexToDelete].outputs;
    _.map(deletedProcessOutputs, (outputProcessRow: FTProcessRow) => {
      _.map(outputProcessRow.childrenProcessRows, (childrenProcessRow: FTProcessRow) => {
        childrenProcessRow._id = '';
      })
    });
    Vue.delete(this.ftFactoryTicket.processes, indexToDelete);
    _.forEach(this.ftFactoryTicket.processes, (process: FTProcess) => {
      if(process.index >= indexToDelete) {
        process.index--;
        _.map(process.outputs, (outputProcessRow: FTProcessRow) => outputProcessRow.relinkChildren())
      }
    })
    this.ftFactoryTicket.createdGoods.length = 0;
    this.ftFactoryTicket.processIndex--;
  }

  addInput(goodsType: string, key: number) {
    const goodsKey = _.findIndex(this.config.inputs.goods, { goodsType });
    if(goodsKey > -1) {
      const newInputs: FTProcessRow[] = [];
      const newInputKey = key + 1;
      for(let i = 0; i <= this.inputs.length; i++) {
        if(i < newInputKey) {
          newInputs[i] = this.inputs[i];
        } else if ( i > newInputKey) {
          newInputs[i] = this.inputs[i-1];
        } else if ( i === newInputKey) {
          newInputs[i] = new FTProcessRow({
            config: { goodsType, type: FTProcessRowTypes.input, key: i },
            ftProcess: this,
          });
        }
      }
      this.inputs = newInputs;
    }
  }

  getHasRightSideButtonsStatus() {
    const inputRightSide = _.some(this.inputs, input => input.showRemoveInputIcon())
    const outputRightSide = false; //_.some(this.outputs, output => output.showRemoveInputIcon())
    return inputRightSide || outputRightSide;
  }

  showAddMoreButtonText(goodsType: string, key: number): boolean {
    return !!this.getAddMoreButtonText(goodsType) && key === _.findLastIndex(this.inputs, { goodsType });
  }

  addOutput() {
    const key = this.outputs.length;
    Vue.set(this.outputs, `${key}`, new FTProcessRow({
      config: {
        type: FTProcessRowTypes.output, key
      },
      ftProcess: this,
    }));
  }
}
